'use client';

import { marked } from 'marked';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { parseWysiwygInput } from '@/lib/utils/wysiwyg';

import { WysiwygRendererProps } from './types';
import { WysiwygCollapsible } from './WysiwygCollapsible';
import { WysiwygContent } from './WysiwygContent';

export const WysiwygRenderer = ({
  html,
  isMarkdown = false,
  collapsible = false,
}: WysiwygRendererProps) => {
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslations();

  const rawHtml = isMarkdown ? (marked.parse(html) as string) : html;

  let htmlToParse: string;
  if (/<\s*[a-z][\s\S]*>/i.test(rawHtml) || !rawHtml) {
    htmlToParse = rawHtml;
  } else {
    const paragraphs = rawHtml
      .split(/\n{2,}/)
      .map(line => `<p>${line.replace(/\n/g, '<br/>')}</p>`);
    htmlToParse = paragraphs.join('');
  }

  const sanitizedHtml = parseWysiwygInput(htmlToParse, isMarkdown);

  useEffect(() => {
    if (!collapsible) return;

    const checkOverflow = () => {
      const tempElement = document.createElement('div');
      tempElement.className = 'wysiwyg';
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.width =
        contentRef.current?.offsetWidth + 'px' || '750px';
      tempElement.innerHTML = html;

      document.body.appendChild(tempElement);

      const fullHeight = tempElement.scrollHeight;
      const computedStyle = window.getComputedStyle(tempElement);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
      const isMobile = window.innerWidth < 768;
      const maxLines = isMobile ? 3 : 4;
      const maxHeight = lineHeight * maxLines;

      const hasOverflow = fullHeight > maxHeight + 10;
      setShouldShowButton(hasOverflow);

      document.body.removeChild(tempElement);
    };

    const timer = setTimeout(checkOverflow, 200);
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [collapsible, html]);

  if (!collapsible) {
    return <WysiwygContent html={sanitizedHtml} />;
  }

  return (
    <WysiwygCollapsible
      id="collection-desc"
      expanded={expanded}
      onToggle={() => setExpanded(prev => !prev)}
      showButton={shouldShowButton}
      showMoreText={t('SHOW_MORE')}
      showLessText={t('SHOW_LESS')}
      contentRef={contentRef}
    >
      <WysiwygContent html={sanitizedHtml} className="wysiwyg" />
    </WysiwygCollapsible>
  );
};
